import type { MemberKind } from '../ports/auth-port.ts';

/**
 * Every notification this product can send, and who gets it how.
 *
 * One catalog rather than a string at each call site, because the two
 * questions a notification raises — "can I turn this off" and "why did I
 * get this" — are unanswerable if the set is implicit. A preference matrix
 * needs something to enumerate, and so does a test that proves a channel is
 * respected.
 */
export const NOTIFICATION_CHANNELS = ['inApp', 'email', 'push'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

/**
 * What an event is about, so a recipient can be sent to the right place.
 *
 * `recordRef` on the item carries the identity; this says what kind of thing
 * it is, which is what decides the route.
 */
export const NOTIFIABLE_RECORDS = [
  'scheduleItem',
  'job',
  'dailyLog',
  'todo',
  'file',
  'comment',
  'bill',
  /**
   * A safety notice carries **no `jobId`**, unlike every other kind here,
   * and that is correct rather than missing: a notice is company-wide.
   * Both route builders already do the right thing with it — `linkFor`
   * sends an email to `/app` and `routeFor` declines to navigate — and
   * `/app` is exactly where somebody chased about a notice needs to
   * land, because the splash is waiting there.
   */
  'safetyNotice',
] as const;
export type NotifiableRecord = (typeof NOTIFIABLE_RECORDS)[number];

export interface NotificationEvent {
  /** Stable key, stored on the item and used by the preference matrix. */
  readonly key: string;
  /** What a person would call it in a settings screen. */
  readonly label: string;
  readonly record: NotifiableRecord;
  /**
   * Default channels per member kind, and the absence of a kind means this
   * event never reaches them.
   *
   * Per kind rather than one list, because the same event means different
   * things to different people: a sub asked to confirm dates needs an email,
   * where the project manager who asked wants it in the app and nowhere
   * else. Defaults, not rules — a person's own preferences override them,
   * except where `required` says otherwise.
   */
  readonly channels: Partial<Record<MemberKind, readonly NotificationChannel[]>>;
  /**
   * Cannot be turned off.
   *
   * Reserved for events where silence would be a worse failure than the
   * noise: being asked to confirm dates you are expected to turn up for is
   * not a preference. Everything else is opt-out, which is why this is a
   * flag rather than the default.
   */
  readonly required?: boolean;
}

/**
 * The catalog.
 *
 * Only the events the product can actually raise today. Adding one is a
 * line here plus the call that raises it, so an event with no sender is
 * visible as an absence rather than hidden among a dozen aspirational keys.
 */
export const NOTIFICATION_EVENTS: readonly NotificationEvent[] = [
  {
    key: 'schedule.confirmationRequested',
    label: 'You are asked to confirm dates',
    record: 'scheduleItem',
    // A sub's own answer is the only write they make to a schedule, and they
    // cannot make it if nobody tells them it is wanted.
    channels: { sub: ['inApp', 'email', 'push'] },
    required: true,
  },
  {
    key: 'schedule.confirmationAnswered',
    label: 'A sub accepted or declined dates',
    record: 'scheduleItem',
    channels: { internal: ['inApp'] },
  },
  {
    key: 'schedule.datesMoved',
    label: 'Dates you were given have moved',
    record: 'scheduleItem',
    // The reason a move on a published item demands an explanation is that
    // somebody is planning around it. Telling them is the other half.
    channels: { sub: ['inApp', 'email'], client: ['inApp', 'email'] },
  },
  {
    key: 'schedule.reminder',
    label: 'Work of yours starts soon',
    record: 'scheduleItem',
    channels: { internal: ['inApp'], sub: ['inApp', 'email'] },
  },
  {
    key: 'comment.mentioned',
    label: 'Somebody named you in a comment',
    record: 'comment',
    // Email for internal staff as well, which `dailyLog.posted` does not
    // get. Being named in a comment is a direct request to one person —
    // closer to being asked to confirm dates than to being told a log
    // exists — and a request nobody sees is worse than noise.
    channels: { internal: ['inApp', 'email'], sub: ['inApp', 'email'], client: ['inApp', 'email'] },
  },
  {
    key: 'dailyLog.posted',
    label: 'A daily log was shared with you',
    record: 'dailyLog',
    // Internal staff only get this when they are named on the log's notify
    // list — a company with four jobs would otherwise send every office
    // user a notification a day per job, which is how a feed stops being
    // read.
    channels: { internal: ['inApp'], sub: ['inApp', 'email'], client: ['inApp', 'email'] },
  },
  {
    key: 'bill.submittedBySub',
    label: 'A sub sent in an invoice',
    record: 'bill',
    // Email as well as the bell, which `dailyLog.posted` does not get for
    // internal staff. A sub's invoice is a request for money with a due
    // date on it: the cost of missing one is a late payment and a phone
    // call, where the cost of missing a daily log is reading it tomorrow.
    channels: { internal: ['inApp', 'email'] },
  },
  {
    key: 'schedule.published',
    label: 'A schedule was published to you',
    record: 'job',
    channels: { sub: ['inApp', 'email'], client: ['inApp', 'email'] },
  },
  {
    key: 'equipment.doubleBooked',
    label: 'A machine you need is booked on another job',
    record: 'job',
    /**
     * **Internal only, and that is deliberate.**
     *
     * A double-booking is the company's own scheduling problem. A sub told
     * that the excavator is also wanted at Saguaro learns nothing they can
     * act on and plenty about another client's job; a homeowner told it
     * reads it as a delay nobody has decided on yet.
     *
     * `inApp` and `email`, not `push`. It is a decision somebody makes at
     * a desk with two schedules open — the day it matters is named in the
     * message — and a phone buzz about next month's loader is how a
     * channel gets turned off.
     *
     * The ticket asked to "email all employees". That is not what this
     * sends: the recipients are the people assigned to the two clashing
     * activities, plus whoever published. An event that mails forty people
     * every time two jobs want one machine is the reason a feed stops
     * being read.
     */
    channels: { internal: ['inApp', 'email'] },
  },
  {
    key: 'safety.acknowledgementOutstanding',
    label: 'You have not acknowledged a safety notice',
    record: 'safetyNotice',
    /**
     * **No `inApp`, and that is the whole design of this event.**
     *
     * The safety splash is a live subscription and cannot be dismissed
     * without acknowledging, so anybody still outstanding is by definition
     * somebody who has not had the app open since the notice went up. An
     * in-app notification would be a row behind a modal they are not
     * looking at — it would arrive at the one place where it is already
     * impossible to miss the notice itself. The channels that can reach
     * them are the ones that do not need the app opened first.
     */
    channels: { internal: ['email', 'push'], sub: ['email', 'push'] },
    /**
     * Not a preference, for the same reason as a confirmation request: a
     * safety notice somebody has been asked to read is not something they
     * get to mute, and this is the only reminder they will get.
     */
    required: true,
  },
];

const BY_KEY = new Map(NOTIFICATION_EVENTS.map((event) => [event.key, event]));

/** The event for a key, or undefined for one this version does not know. */
export function notificationEvent(key: string): NotificationEvent | undefined {
  return BY_KEY.get(key);
}

/**
 * The channels an event reaches this kind of member on.
 *
 * Empty for a kind the event does not address, which is the useful answer:
 * "nothing is sent" rather than "the default is nothing".
 */
export function defaultChannels(
  event: NotificationEvent,
  kind: MemberKind,
): readonly NotificationChannel[] {
  return event.channels[kind] ?? [];
}

/**
 * Whether this event reaches this kind of member at all.
 *
 * Asked before building an item, so an event that cannot reach somebody
 * does not create a row nobody will ever read.
 */
export function reaches(event: NotificationEvent, kind: MemberKind): boolean {
  return defaultChannels(event, kind).length > 0;
}
